/**
 * Zone / language → country flag + geographic size, resolved deterministically
 * in the frontend (zero LLM, zero network, O(1) lookups). The dashboard reads
 * these off zone labels (real place names like "Paris" / "France") and ISO
 * language codes — no per-render classifier call.
 *
 * Coverage is French-first (the product's primary market): all countries in
 * FR + EN, major world metros, continents, and the "Global" pseudo-zone. An
 * unresolved long-tail label falls back to a neutral pin (size only).
 *
 * Flag images: flagcdn.com SVG CDN (`https://flagcdn.com/<iso2>.svg`) — crisp
 * on every OS incl. Windows, no bundled assets. See <Flag> in geoFlags.tsx.
 */

export type ZoneSize = 'world' | 'continent' | 'country' | 'region' | 'city' | 'village';

export interface ResolvedZone {
  /** ISO 3166-1 alpha-2, lowercase (flagcdn key). Absent for world/continent. */
  countryCode?: string;
  size: ZoneSize;
}

const norm = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/\s+/g, ' ');

// ─── Complete country map from Intl.DisplayNames (fr + en) ──────────────────
// Every ISO 3166-1 alpha-2 country, localized in BOTH languages, built once at
// module load. This is the exhaustive base layer (fixes gaps like Namibia that
// a hand-list misses in either language); the ALIAS dict below overrides it for
// common abbreviations / colloquial spellings Intl doesn't emit (UK, USA, uae…).
// A tiny alpha-2 seed drives the enumeration (no external ISO list needed).
const ISO2: string[] = (() => {
  const codes: string[] = [];
  for (let a = 65; a <= 90; a++)
    for (let b = 65; b <= 90; b++) codes.push(String.fromCharCode(a) + String.fromCharCode(b));
  return codes;
})();

const INTL_COUNTRY: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  try {
    const dn = {
      fr: new Intl.DisplayNames(['fr'], { type: 'region' }),
      en: new Intl.DisplayNames(['en'], { type: 'region' }),
    };
    for (const code of ISO2) {
      for (const lang of ['fr', 'en'] as const) {
        let name: string | undefined;
        try {
          name = dn[lang].of(code);
        } catch {
          name = undefined;
        }
        // Intl returns the code itself for unassigned regions — skip those.
        if (name && name !== code) out[norm(name)] = code.toLowerCase();
      }
    }
  } catch {
    // Intl.DisplayNames unavailable (very old runtime) — ALIAS + token fallback still work.
  }
  return out;
})();

// ─── Common aliases / abbreviations (override Intl where needed) ────────────
// Kept hand-curated: colloquial names + abbreviations Intl.DisplayNames won't
// produce (UK, USA, uae, hollande…). Merged OVER the Intl base.
const COUNTRY_ALIASES: Record<string, string> = {
  // France & FR-speaking
  france: 'fr', belgique: 'be', belgium: 'be', suisse: 'ch', switzerland: 'ch',
  luxembourg: 'lu', monaco: 'mc', canada: 'ca',
  // Europe
  'royaume-uni': 'gb', 'royaume uni': 'gb', 'united kingdom': 'gb', uk: 'gb',
  angleterre: 'gb', england: 'gb', allemagne: 'de', germany: 'de',
  espagne: 'es', spain: 'es', italie: 'it', italy: 'it',
  portugal: 'pt', 'pays-bas': 'nl', 'pays bas': 'nl', netherlands: 'nl', hollande: 'nl',
  irlande: 'ie', ireland: 'ie', autriche: 'at', austria: 'at',
  pologne: 'pl', poland: 'pl', suede: 'se', sweden: 'se',
  norvege: 'no', norway: 'no', danemark: 'dk', denmark: 'dk',
  finlande: 'fi', finland: 'fi', grece: 'gr', greece: 'gr',
  'republique tcheque': 'cz', 'czech republic': 'cz', czechia: 'cz',
  roumanie: 'ro', romania: 'ro', hongrie: 'hu', hungary: 'hu',
  bulgarie: 'bg', bulgaria: 'bg', croatie: 'hr', croatia: 'hr',
  slovaquie: 'sk', slovakia: 'sk', slovenie: 'si', slovenia: 'si',
  ukraine: 'ua', russie: 'ru', russia: 'ru', turquie: 'tr', turkey: 'tr',
  // Americas
  'etats-unis': 'us', 'etats unis': 'us', 'united states': 'us', usa: 'us', 'u.s.': 'us', amerique: 'us',
  mexique: 'mx', mexico: 'mx', bresil: 'br', brazil: 'br',
  argentine: 'ar', argentina: 'ar', chili: 'cl', chile: 'cl',
  colombie: 'co', colombia: 'co', perou: 'pe', peru: 'pe',
  // Africa & ME
  maroc: 'ma', morocco: 'ma', algerie: 'dz', algeria: 'dz',
  tunisie: 'tn', tunisia: 'tn', senegal: 'sn', 'cote d\'ivoire': 'ci', 'ivory coast': 'ci',
  egypte: 'eg', egypt: 'eg', 'afrique du sud': 'za', 'south africa': 'za',
  nigeria: 'ng', kenya: 'ke',
  'emirats arabes unis': 'ae', uae: 'ae', 'arabie saoudite': 'sa', 'saudi arabia': 'sa',
  israel: 'il', qatar: 'qa',
  // Asia & Oceania
  chine: 'cn', china: 'cn', japon: 'jp', japan: 'jp',
  inde: 'in', india: 'in', 'coree du sud': 'kr', 'south korea': 'kr', coree: 'kr',
  singapour: 'sg', singapore: 'sg', 'hong kong': 'hk',
  thailande: 'th', thailand: 'th', vietnam: 'vn', indonesie: 'id', indonesia: 'id',
  malaisie: 'my', malaysia: 'my', philippines: 'ph',
  australie: 'au', australia: 'au', 'nouvelle-zelande': 'nz', 'new zealand': 'nz',
};

// Final country map: exhaustive Intl base (all ISO countries, fr+en) with the
// hand-curated aliases layered on top.
const COUNTRY: Record<string, string> = { ...INTL_COUNTRY, ...COUNTRY_ALIASES };

// ─── Major metros / regions → ISO2 (city/region size) ───────────────────────
const CITY: Record<string, string> = {
  // France (cities + regions)
  paris: 'fr', lyon: 'fr', marseille: 'fr', toulouse: 'fr', nice: 'fr', nantes: 'fr',
  strasbourg: 'fr', montpellier: 'fr', bordeaux: 'fr', lille: 'fr', rennes: 'fr',
  reims: 'fr', 'saint-etienne': 'fr', toulon: 'fr', grenoble: 'fr', dijon: 'fr',
  angers: 'fr', nimes: 'fr', clermont: 'fr', 'clermont-ferrand': 'fr', brest: 'fr',
  'ile-de-france': 'fr', 'ile de france': 'fr', 'auvergne-rhone-alpes': 'fr',
  occitanie: 'fr', bretagne: 'fr', normandie: 'fr', 'pays de la loire': 'fr',
  'nouvelle-aquitaine': 'fr', 'grand est': 'fr', 'hauts-de-france': 'fr',
  paca: 'fr', "provence-alpes-cote d'azur": 'fr', 'centre-val de loire': 'fr',
  corse: 'fr', 'bourgogne-franche-comte': 'fr',
  // Europe metros
  londres: 'gb', london: 'gb', berlin: 'de', munich: 'de', munchen: 'de', hambourg: 'de', hamburg: 'de',
  francfort: 'de', frankfurt: 'de', cologne: 'de', madrid: 'es', barcelone: 'es', barcelona: 'es',
  valence: 'es', valencia: 'es', seville: 'es', rome: 'it', milan: 'it', milano: 'it',
  naples: 'it', turin: 'it', lisbonne: 'pt', lisbon: 'pt', porto: 'pt',
  amsterdam: 'nl', rotterdam: 'nl', bruxelles: 'be', brussels: 'be', anvers: 'be', antwerp: 'be',
  geneve: 'ch', geneva: 'ch', zurich: 'ch', lausanne: 'ch', bale: 'ch', basel: 'ch',
  vienne: 'at', vienna: 'at', dublin: 'ie', stockholm: 'se', oslo: 'no', copenhague: 'dk', copenhagen: 'dk',
  helsinki: 'fi', athenes: 'gr', athens: 'gr', varsovie: 'pl', warsaw: 'pl', prague: 'cz',
  // Americas metros
  'new york': 'us', 'los angeles': 'us', chicago: 'us', 'san francisco': 'us', boston: 'us',
  miami: 'us', seattle: 'us', austin: 'us', 'washington': 'us', atlanta: 'us', dallas: 'us',
  toronto: 'ca', montreal: 'ca', vancouver: 'ca', quebec: 'ca', ottawa: 'ca',
  'sao paulo': 'br', 'mexico city': 'mx', 'buenos aires': 'ar',
  // Asia / ME / Oceania metros
  tokyo: 'jp', osaka: 'jp', pekin: 'cn', beijing: 'cn', shanghai: 'cn', shenzhen: 'cn',
  seoul: 'kr', mumbai: 'in', delhi: 'in', bangalore: 'in', bengaluru: 'in',
  dubai: 'ae', 'abou dabi': 'ae', 'abu dhabi': 'ae', riyad: 'sa', riyadh: 'sa',
  bangkok: 'th', jakarta: 'id', 'kuala lumpur': 'my', manille: 'ph', manila: 'ph',
  sydney: 'au', melbourne: 'au', auckland: 'nz', 'tel aviv': 'il',
  // Africa metros
  casablanca: 'ma', tunis: 'tn', alger: 'dz', algiers: 'dz', dakar: 'sn',
  johannesburg: 'za', johannesbourg: 'za', 'le cap': 'za', 'cape town': 'za',
  pretoria: 'za', durban: 'za', 'le caire': 'eg', cairo: 'eg', lagos: 'ng',
  abuja: 'ng', nairobi: 'ke', accra: 'gh', abidjan: 'ci', windhoek: 'na',
  harare: 'zw', gaborone: 'bw', kampala: 'ug', 'addis-abeba': 'et', 'addis ababa': 'et',
  'dar es salaam': 'tz', luanda: 'ao', maputo: 'mz', kigali: 'rw',
};

// Continents / worldwide pseudo-zones (no country flag).
const CONTINENT = new Set(
  ['europe', 'afrique', 'africa', 'asie', 'asia', 'amerique du nord', 'north america',
   'amerique du sud', 'south america', 'amerique latine', 'latin america', 'oceanie', 'oceania',
   'moyen-orient', 'middle east', 'amerique', 'americas'].map(norm),
);
const WORLD = new Set(['global', 'monde', 'worldwide', 'world', 'international', 'mondial'].map(norm));

// Onboarding `scale` tag (FR) → ZoneSize. Used when a persisted scale exists.
export const SCALE_TO_SIZE: Record<string, ZoneSize> = {
  monde: 'world',
  europe: 'continent',
  pays: 'country',
  region: 'region',
  ville: 'city',
  village: 'village',
};

/**
 * Resolve a zone label to a flag country + size. `storedScale`/`storedCountry`
 * (from a persisted Location, when available) win over the dictionary.
 */
export function resolveZone(
  label: string,
  stored?: { countryCode?: string; scale?: string },
): ResolvedZone {
  const storedSize = stored?.scale ? SCALE_TO_SIZE[stored.scale.toLowerCase()] : undefined;
  if (stored?.countryCode) {
    return { countryCode: stored.countryCode.toLowerCase(), size: storedSize ?? 'country' };
  }

  const k = norm(label);
  if (WORLD.has(k)) return { size: 'world' };
  if (CONTINENT.has(k)) return { size: 'continent' };
  if (COUNTRY[k]) return { countryCode: COUNTRY[k], size: storedSize ?? 'country' };
  if (CITY[k]) return { countryCode: CITY[k], size: storedSize ?? 'city' };

  // Token fallback: "Lyon, France" / "Région Paris" → match any known token.
  for (const tok of k.split(/[,\-/()]| /).filter(Boolean)) {
    if (COUNTRY[tok]) return { countryCode: COUNTRY[tok], size: storedSize ?? 'country' };
    if (CITY[tok]) return { countryCode: CITY[tok], size: storedSize ?? 'city' };
  }
  return { size: storedSize ?? 'region' };
}

// ─── Language (ISO 639-1) → representative flag country ─────────────────────
// A language isn't a country; we use the most-recognized flag per language.
const LANG_FLAG: Record<string, string> = {
  fr: 'fr', en: 'gb', es: 'es', de: 'de', it: 'it', pt: 'pt', nl: 'nl',
  pl: 'pl', sv: 'se', da: 'dk', no: 'no', fi: 'fi', el: 'gr', cs: 'cz',
  ru: 'ru', tr: 'tr', ar: 'sa', zh: 'cn', ja: 'jp', ko: 'kr', hi: 'in',
};

export function languageFlag(code: string): string | undefined {
  return LANG_FLAG[(code || '').toLowerCase()];
}
